import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const EmployeeTable = ({ 
  employees, 
  onEmployeeSelect, 
  onEmployeeEdit, 
  onEmployeeToggle, 
  onEmployeeDelete,
  onPhotoUpload,
  selectedEmployees,
  onSelectionChange,
  sortConfig,
  onSort,
  isColumnsHidden = false
}) => {
  
  // Helper function to calculate Gehalt S for employees (cumulative)
  const calculateGehaltS = (employee) => {
    // Use the cumulative Gehalt S value without monthly reset
    return employee?.totalGehaltS || employee?.gehaltS || 0;
  };
  const [hoveredRow, setHoveredRow] = useState(null);

  const sortedEmployees = useMemo(() => {
    if (!sortConfig?.key) return employees;

    return [...employees]?.sort((a, b) => {
      const getValue = (emp, key) => {
        if (key === 'gehaltT') {
          const gehaltSValue = calculateGehaltS(emp);
          const salaryValue = emp?.salary || 0;
          return (gehaltSValue || 0) - (salaryValue || 0);
        }
        return emp?.[key];
      };

      const aValue = getValue(a, sortConfig?.key);
      const bValue = getValue(b, sortConfig?.key);

      if (aValue < bValue) {
        return sortConfig?.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig?.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [employees, sortConfig]);

  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange(employees?.map(emp => emp?.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectEmployee = (employeeId, checked) => {
    if (checked) {
      onSelectionChange([...selectedEmployees, employeeId]);
    } else {
      onSelectionChange(selectedEmployees?.filter(id => id !== employeeId));
    }
  };

  const getSortIcon = (column) => {
    if (sortConfig?.key !== column) {
      return <Icon name="ArrowUpDown" size={14} className="text-text-secondary" />;
    }
    return sortConfig?.direction === 'asc' 
      ? <Icon name="ArrowUp" size={14} className="text-primary" />
      : <Icon name="ArrowDown" size={14} className="text-primary" />;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-success/10 text-success border-success/20', label: 'Aktiv' },
      inactive: { color: 'bg-error/10 text-error border-error/20', label: 'Inaktiv' },
      suspended: { color: 'bg-warning/10 text-warning border-warning/20', label: 'Gesperrt' }
    };

    const config = statusConfig?.[status] || statusConfig?.active;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const allSelected = selectedEmployees?.length === employees?.length && employees?.length > 0;
  const someSelected = selectedEmployees?.length > 0 && selectedEmployees?.length < employees?.length;

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden elevation-1">
      {/* Table Header */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="w-12 px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={input => {
                    if (input) input.indeterminate = someSelected;
                  }}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                  className="rounded border-border focus:ring-primary focus:ring-2"
                />
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('photo')}
                  className="flex items-center space-x-2 text-sm font-medium text-text-primary hover:text-primary transition-colors"
                >
                  <span>Foto</span>
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('name')}
                  className="flex items-center space-x-2 text-sm font-medium text-text-primary hover:text-primary transition-colors"
                >
                  <span>Name</span>
                  {getSortIcon('name')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('employeeCode')}
                  className="flex items-center space-x-2 text-sm font-medium text-text-primary hover:text-primary transition-colors"
                >
                  <span>Mitarbeiternummer</span>
                  {getSortIcon('employeeCode')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('hireDate')}
                  className="flex items-center space-x-2 text-sm font-medium text-text-primary hover:text-primary transition-colors"
                >
                  <span>Einstellungsdatum</span>
                  {getSortIcon('hireDate')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('hireType')}
                  className="flex items-center space-x-2 text-sm font-medium text-text-primary hover:text-primary transition-colors"
                >
                  <span>Anstellungsart</span>
                  {getSortIcon('hireType')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('salary')}
                  className="flex items-center space-x-2 text-sm font-medium text-text-primary hover:text-primary transition-colors"
                >
                  <span>Gehalt</span>
                  {getSortIcon('salary')}
                </button>
              </th>
              {!isColumnsHidden && (
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => onSort('gehaltS')}
                    className="flex items-center space-x-2 text-sm font-medium text-text-primary hover:text-primary transition-colors"
                  >
                    <span>Gehalt S</span>
                    {getSortIcon('gehaltS')}
                  </button>
                </th>
              )}
              {!isColumnsHidden && (
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => onSort('gehaltT')}
                    className="flex items-center space-x-2 text-sm font-medium text-text-primary hover:text-primary transition-colors"
                  >
                    <span>Gehalt T</span>
                    {getSortIcon('gehaltT')}
                  </button>
                </th>
              )}
              {!isColumnsHidden && (
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => onSort('tagesrate')}
                    className="flex items-center space-x-2 text-sm font-medium text-text-primary hover:text-primary transition-colors"
                  >
                    <span>Tagesrate</span>
                    {getSortIcon('tagesrate')}
                  </button>
                </th>
              )}
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('status')}
                  className="flex items-center space-x-2 text-sm font-medium text-text-primary hover:text-primary transition-colors"
                >
                  <span>Status</span>
                  {getSortIcon('status')}
                </button>
              </th>
              <th className="px-4 py-3 text-right">
                <span className="text-sm font-medium text-text-primary">Aktionen</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedEmployees?.map((employee) => (
              <tr
                key={employee?.id}
                className={`hover:bg-muted/50 transition-colors cursor-pointer ${
                  selectedEmployees?.includes(employee?.id) ? 'bg-primary/5' : ''
                }`}
                onMouseEnter={() => setHoveredRow(employee?.id)}
                onMouseLeave={() => setHoveredRow(null)}
                onClick={() => onEmployeeSelect(employee)}
              >
                <td className="px-4 py-3" onClick={(e) => e?.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedEmployees?.includes(employee?.id)}
                    onChange={(e) => handleSelectEmployee(employee?.id, e?.target?.checked)}
                    className="rounded border-border focus:ring-primary focus:ring-2"
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-muted flex items-center justify-center">
                    {employee?.photo ? (
                      <Image
                        src={employee?.photo}
                        alt={employee?.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Icon name="User" size={20} className="text-text-secondary" />
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div>
                    <div className="font-medium text-text-primary">{employee?.name}</div>
                    <div className="text-sm text-text-secondary">{employee?.email}</div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="font-mono text-sm text-text-primary">{employee?.employeeCode}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-text-primary">
                    {new Date(employee.hireDate)?.toLocaleDateString('de-DE')}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-text-primary">
                    {employee?.hireType}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-text-primary font-medium">
                    {employee?.salary ? `€${employee.salary.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '€0,00'}
                  </span>
                </td>
                {!isColumnsHidden && (
                  <td className="px-4 py-3">
                    <span className="text-sm text-text-primary font-medium">
                      {(() => {
                        const gehaltSValue = calculateGehaltS(employee);
                        return gehaltSValue ? `€${gehaltSValue.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '€0,00';
                      })()}
                    </span>
                  </td>
                )}
                {!isColumnsHidden && (
                  <td className="px-4 py-3">
                    <span className="text-sm text-text-primary font-medium">
                      {(() => {
                        const gehaltSValue = calculateGehaltS(employee) || 0;
                        const salaryValue = employee?.salary || 0;
                        const gehaltTValue = gehaltSValue - salaryValue;
                        return `€${gehaltTValue.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                      })()}
                    </span>
                  </td>
                )}
                {!isColumnsHidden && (
                  <td className="px-4 py-3">
                    <span className="text-sm text-text-primary font-medium">
                      {employee?.tagesrate ? `€${employee.tagesrate.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/Tag` : '€0,00/Tag'}
                    </span>
                  </td>
                )}
                <td className="px-4 py-3">
                  {getStatusBadge(employee?.status)}
                </td>
                <td className="px-4 py-3" onClick={(e) => e?.stopPropagation()}>
                  <div className="flex items-center justify-end space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEmployeeEdit(employee)}
                      iconName="Edit"
                      className="touch-feedback"
                      title="Mitarbeiter bearbeiten"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onPhotoUpload(employee)}
                      iconName="Camera"
                      className="touch-feedback"
                      title="Foto hochladen"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEmployeeToggle(employee)}
                      iconName={employee?.status === 'active' ? 'UserX' : 'UserCheck'}
                      className="touch-feedback"
                      title={employee?.status === 'active' ? 'Mitarbeiter deaktivieren' : 'Mitarbeiter aktivieren'}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEmployeeDelete(employee)}
                      iconName="Trash2"
                      className="touch-feedback text-error hover:text-error hover:bg-error/10"
                      title="Mitarbeiter löschen"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Empty State */}
      {employees?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Users" size={48} className="text-text-secondary mx-auto mb-4" />
          <h3 className="text-lg font-medium text-text-primary mb-2">Keine Mitarbeiter gefunden</h3>
          <p className="text-text-secondary mb-4">Beginnen Sie mit dem Hinzufügen Ihres ersten Mitarbeiters</p>
          <Button variant="default" iconName="Plus" iconPosition="left">
            Mitarbeiter hinzufügen
          </Button>
        </div>
      )}
    </div>
  );
};

export default EmployeeTable;